import React, { useEffect, useContext, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useForm from "react-hook-form";
import moment from "moment";
import ReactTable from "react-table";
import Datetime from "react-datetime";

import AssignmentTurnedIn from "@material-ui/icons/AssignmentTurnedIn";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Select from "react-select";

import GridContainer from "beaktShared/components/Grid/GridContainer.js";
import GridItem from "beaktShared/components/Grid/GridItem.js";
import Button from "beaktShared/components/CustomButtons/Button.js";
import Card from "beaktShared/components/Card/Card.js";
import CardBody from "beaktShared/components/Card/CardBody.js";
import CardIcon from "beaktShared/components/Card/CardIcon.js";
import CardHeader from "beaktShared/components/Card/CardHeader.js";
import SnackbarContent from "beaktShared/components/Snackbar/SnackbarContent.js";
import {
  formateDate,
  convertDate,
  validateDate,
  getLocale,
  getStringFormatDate,
} from "beaktShared/helpers/masks";
import { ValidSelect } from "beaktShared/helpers/validselect";
import { UseGetApiSimple, UsePostApi } from "beaktShared/services/crudeApiService";
import { SessionContext, getTokenCookie } from "beaktShared/services/session";

import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/crudeStyle.js";

const useStyles = makeStyles(styles);
const MAX_PERIODO_DIAS = 90;
const tenantId = window.location.host.split(":")[0].split(".")[0];

const opcoesStatus = [
  { value: null, label: "Todos" },
  { value: "Aguardando OK para pagamento", label: "Aguardando OK para pagamento" },
  { value: "Aguardando execução do pagamento", label: "Aguardando execução do pagamento" },
  { value: "Pagamento efetuado", label: "Pagamento efetuado" },
];

const opcoesSituacao = [
  { value: null, label: "Todas" },
  { value: "Credor", label: "Saldo Credor" },
  { value: "Devedor", label: "Saldo Devedor" },
];

const formatDateTime = (value) => {
  if (!value) return "";
  return moment(value).format("DD/MM/YYYY HH:mm:ss");
};

const getStatusLabel = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const found = opcoesStatus.find(
    (o) => o.value !== null && String(o.value) === String(value)
  );
  return found ? found.label : value;
};

const getStatusStyle = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const v = String(value).toLowerCase().trim();
  // Pagamento efetuado / Confirmação efetuada
  if (v === "2" || v.includes("pagamento efetuad") || v.includes("confirmação efetuad") || v.includes("confirmacao efetuad"))
    return { color: "#2e7d32", background: "#e8f5e9" };
  // Fechamento Estornado
  if (v === "3" || v.includes("estornado"))
    return { color: "#c62828", background: "#fce4ec" };
  // Aguardando execução do pagamento
  if (v === "1" || v.includes("execu"))
    return { color: "#f57f17", background: "#fff9c4" };
  // Aguardando OK para pagamento
  if (v === "0" || v.includes("aguard") || v.includes("ok para"))
    return { color: "#1565c0", background: "#e3f2fd" };
  return null;
};

const acaoConfig = [
  {
    match: ["confirmacaopagamento", "confirmação de pagamento", "confirmacao de pagamento"],
    label: "Confirmação de Pagamento",
    color: "#2e7d32",
    background: "#e8f5e9",
  },
  {
    match: ["reprocessamento"],
    label: "Reprocessamento",
    color: "#e65100",
    background: "#fff3e0",
  },
  {
    match: ["estorno"],
    label: "Estorno",
    color: "#c62828",
    background: "#fce4ec",
  },
  {
    match: ["fechamentorede", "fechamento rede"],
    label: "Fechamento Rede",
    color: "#1565c0",
    background: "#e3f2fd",
  },
  {
    match: ["fechamentounidade", "fechamento unidade"],
    label: "Fechamento Unidade",
    color: "#1565c0",
    background: "#e3f2fd",
  },
  {
    match: ["prorrogarrecebimento", "prorrogar recebimento"],
    label: "Prorrogar Recebimento",
    color: "#6a1b9a",
    background: "#f3e5f5",
  },
  {
    match: ["confirmaraceite", "confirmar aceite"],
    label: "Confirmar Aceite",
    color: "#00838f",
    background: "#e0f7fa",
  },
  {
    match: ["confirmarrecebimento", "confirmar recebimento"],
    label: "Confirmar Recebimento",
    color: "#303f9f",
    background: "#e8eaf6",
  },
  {
    match: ["consentimentoinauguracao", "consentimento de inauguração", "consentimento de inauguracao"],
    label: "Consentimento de Inauguração",
    color: "#f9a825",
    background: "#fffde7",
  },
];

const getAcaoInfo = (value) => {
  if (!value) return null;
  const key = value.toLowerCase().replace(/\s+/g, "");
  for (const cfg of acaoConfig) {
    if (cfg.match.some((m) => m.replace(/\s+/g, "") === key)) return cfg;
  }
  return { label: value, color: "#6a1b9a", background: "#f3e5f5" };
};

const mapTipoAcaoOptions = (items) => {
  if (!items || !items.length) return [];
  return items.map((item) => ({
    value: item.id ?? item.codigo ?? item.value ?? item.descricao ?? item.nome ?? item.label,
    label: item.descricao ?? item.nome ?? item.label ?? item.value,
  }));
};

export default function AuditoriaRoyalties() {
  const { t } = useTranslation();
  const classes = useStyles();
  const session = useContext(SessionContext);
  const { register, setValue, errors, setError, clearError } = useForm({
    mode: "onBlur",
  });

  const [dtini, setdtini] = useState(
    formateDate(moment(new Date()).subtract(90, "days"))
  );
  const [dtfim, setdtfim] = useState(formateDate(moment(new Date())));

  const [estabelecimentosSelecionados, setEstabelecimentosSelecionados] = useState([]);
  const [estabelecimentos, setEstabelecimentos] = useState([]);
  const [loadingEstabelecimentos, setLoadingEstabelecimentos] = useState(false);

  const [statusFechamento, setStatusFechamento] = useState(opcoesStatus[0]);
  const [situacaoFechamento, setSituacaoFechamento] = useState(opcoesSituacao[0]);
  const [tipoAcao, setTipoAcao] = useState(null);
  const [tiposAcao, setTiposAcao] = useState([]);

  const [resultado, setResultado] = useState(null);
  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingXlsx, setLoadingXlsx] = useState(false);

  const [notification, setNotification] = useState({
    show: false,
    color: "info",
    message: "",
  });

  const showNotification = (message, color) => {
    setNotification({ show: true, color, message });
    setTimeout(() => setNotification({ show: false }), 6000);
  };

  useEffect(() => {
    setValue("dtini", dtini);
    setValue("dtfim", dtfim);

    UseGetApiSimple("royalties/auditoria/tiposacao", 0).then((result) => {
      if (result.status === 200) {
        setTiposAcao(mapTipoAcaoOptions(result.data));
      }
    });
    
    setLoadingEstabelecimentos(true);
    UseGetApiSimple(
      "estabelecimentos/ativos/byfranquia/" + session.franquiaId,
      0
    ).then((result) => {
      setLoadingEstabelecimentos(false);
      if (result.status === 200) {
        setEstabelecimentos(result.data);
      }
    });
  }, [session.franquiaId]);

  const buildBody = useCallback(
    () => ({
      franquiaId: session.franquiaId,
      dataInicial: convertDate(dtini),
      dataFinal: convertDate(dtfim),
      estabelecimentos: estabelecimentosSelecionados,
      statusFechamento: statusFechamento ? statusFechamento.value : null,
      situacaoFechamento: situacaoFechamento ? situacaoFechamento.value : null,
      tipoAcao: tipoAcao?.value ?? null,
    }),
    [
      session.franquiaId,
      dtini,
      dtfim,
      estabelecimentosSelecionados,
      statusFechamento,
      situacaoFechamento,
      tipoAcao,
    ]
  );

  const handleEstabelecimentoChange = (values) => {
    const lstsAdd = [];
    (values || []).map((item) => {
      lstsAdd.push(item.estabelecimentoId);
    });
    setEstabelecimentosSelecionados(lstsAdd);
    clearError("estabelecimentos");
  };

  const handleClickSelecionarTodosEstabelecimentos = () => {
    const lstsAdd = [];
    estabelecimentos.map((item) => {
      lstsAdd.push(item.estabelecimentoId);
    });
    setEstabelecimentosSelecionados(lstsAdd);
    clearError("estabelecimentos");
  };

  const validar = () => {
    if (!dtini || dtini === "") {
      setError("dtini", "required", t("label.required"));
      return false;
    }
    clearError("dtini");

    if (!dtfim || dtfim === "") {
      setError("dtfim", "required", t("label.required"));
      return false;
    }
    clearError("dtfim");

    if (!validateDate(dtini)) {
      setError("dtini", "invaliddate", t("error_message.invalid_date"));
      return false;
    }
    clearError("dtini");

    if (!validateDate(dtfim)) {
      setError("dtfim", "invaliddate", t("error_message.invalid_date"));
      return false;
    }
    clearError("dtfim");

    const dataIni = convertDate(dtini);
    const dataFim = convertDate(dtfim);

    if (dataIni > dataFim) {
      showNotification(
        "Data inicial deve ser anterior à data final.",
        "warning"
      );
      return false;
    }

    const diffDias = Math.ceil(
      (dataFim - dataIni) / (1000 * 60 * 60 * 24)
    );
    if (diffDias > MAX_PERIODO_DIAS) {
      showNotification(
        `O período de consulta não pode exceder ${MAX_PERIODO_DIAS} dias.`,
        "warning"
      );
      return false;
    }

    if (
      !estabelecimentosSelecionados ||
      estabelecimentosSelecionados.length === 0
    ) {
      setError(
        "estabelecimentos",
        "required",
        t("label.select_an_establishment") ||
          "Selecione ao menos um estabelecimento."
      );
      return false;
    }
    clearError("estabelecimentos");

    return true;
  };

  const consultar = () => {
    if (!validar()) return;

    setLoading(true);
    setBuscaRealizada(true);

    UsePostApi("royalties/auditoria", buildBody()).then((result) => {
      setLoading(false);
      if (result.status === 200) {
        setResultado(result.data);
      } else {
        setResultado(null);
        const msg =
          typeof result.message === "string"
            ? result.message
            : result.message?.message || "Erro ao carregar auditoria";
        showNotification(msg, "danger");
      }
    });
  };

  const handleClickPesquisa = () => {
    setResultado(null);
    consultar();
  };

  const downloadAuditoriaXlsx = async () => {
    if (!validar()) return;

    const token = getTokenCookie();
    if (!token || !token.accessToken) {
      showNotification("Sessão expirada.", "danger");
      return;
    }

    setLoadingXlsx(true);
    const url =
      process.env.REACT_APP_API_ENDPOINT + "api/royalties/auditoria/xlsx";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.accessToken}`,
          tenantId: tenantId,
        },
        body: JSON.stringify(buildBody()),
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `AuditoriaRoyalties_${moment().format("DDMMYYYYHHmmss")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      } else {
        showNotification("Erro ao exportar XLSX", "danger");
      }
    } catch {
      showNotification("Erro ao exportar XLSX", "danger");
    }
    setLoadingXlsx(false);
  };

  const columns = [
    {
      Header: "Estabelecimento",
      accessor: "estabelecimento",
      minWidth: 200,
      filterable: false,
    },
    {
      Header: "Mês Ano",
      accessor: "mesAno",
      minWidth: 100,
      filterable: false,
    },
    {
      Header: "Status",
      accessor: "status",
      filterable: false,
      style: { whiteSpace: "normal" },
      Cell: ({ value }) => {
        const st = getStatusStyle(value);
        if (!st) return <span>—</span>;
        return (
          <span style={{
            display: "inline-block",
            color: st.color,
            background: st.background,
            padding: "3px 8px",
            borderRadius: "4px",
            fontWeight: 600,
            fontSize: "12px",
            lineHeight: "1.4",
            wordBreak: "break-word",
          }}>
            {getStatusLabel(value)}
          </span>
        );
      },
    },
    {
      Header: "Data e Hora",
      accessor: "dataHora",
      width: 160,
      filterable: false,
      Cell: ({ value }) => <span>{formatDateTime(value)}</span>,
    },
    {
      Header: "Ação Realizada",
      accessor: "acaoRealizada",
      minWidth: 160,
      filterable: false,
      style: { whiteSpace: "normal" },
      Cell: ({ value }) => {
        const info = getAcaoInfo(value);
        if (!info) return <span>—</span>;
        return (
          <span style={{
            display: "inline-block",
            color: info.color,
            background: info.background,
            padding: "3px 8px",
            borderRadius: "4px",
            fontWeight: 600,
            fontSize: "12px",
            lineHeight: "1.4",
            wordBreak: "break-word",
          }}>
            {info.label}
          </span>
        );
      },
    },
    {
      Header: "Usuário",
      accessor: "nomeUsuario",
      width: 160,
      filterable: false,
    },
    {
      Header: "Observação",
      accessor: "observacao",
      minWidth: 200,
      filterable: false,
    },
  ];

  const dadosTabela = resultado && resultado.dados ? resultado.dados : [];

  return (
    <div>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <AssignmentTurnedIn />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {t('sidebar.royalties_audit')}
              </h4>
              <p>Filtros de Consulta</p>
            </CardHeader>
            <CardBody>
              <GridContainer style={{ marginBottom: '20px'}}>
                <GridItem xs={12} sm={12} md={12}>
                  <FormControl
                    fullWidth
                    className={classes.formControl}
                    style={{ marginBottom: "20px" }}
                  >
                    <label
                      className={
                        errors.estabelecimentos
                          ? "select__label select__label_error"
                          : "select__label"
                      }
                    >
                      {t("label.establishment")} *{" "}
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      classNamePrefix="select"
                      isSearchable
                      styles={ValidSelect(errors.estabelecimentos ? true : false)}
                      isMulti={true}
                      isLoading={loadingEstabelecimentos}
                      name="estabelecimentos"
                      placeholder="Selecione..."
                      options={estabelecimentos}
                      getOptionLabel={({ descricao }) => descricao}
                      getOptionValue={({ estabelecimentoId }) =>
                        estabelecimentoId
                      }
                      value={estabelecimentos.filter(
                        ({ estabelecimentoId }) =>
                          estabelecimentosSelecionados.indexOf(estabelecimentoId) !== -1
                      )}
                      onChange={(value) =>
                        handleEstabelecimentoChange(value || [])
                      }
                      ref={() => register({ name: "estabelecimentos" })}
                      noOptionsMessage={() =>
                        loadingEstabelecimentos
                          ? "Carregando estabelecimentos..."
                          : "Não existe(m) estabelecimento(s) cadastrado(s)"
                      }
                    />

                    {errors.estabelecimentos && (
                      <FormHelperText>
                        <span className="obg">
                          {errors.estabelecimentos.message}
                        </span>
                      </FormHelperText>
                    )}

                    <div style={{ textAlign: "right", marginTop: "3px" }}>
                      <a
                        href="#"
                        onClick={() =>
                          handleClickSelecionarTodosEstabelecimentos()
                        }
                        style={{ marginTop: "10px" }}
                      >
                        Selecionar todos Estabelecimento
                      </a>
                    </div>
                  </FormControl>
                </GridItem>
              </GridContainer>

              <GridContainer style={{ marginBottom: '20px'}}>
                <GridItem xs={12} sm={6} md={3}>
                  <FormControl fullWidth className={classes.formControl} style={{ marginBottom: "20px" }}>
                    <label
                      className={
                        errors.dtini
                          ? "select__label select__label_error"
                          : "select__label"
                      }
                    >
                      Data Inicial *
                    </label>
                    <Datetime
                      locale={getLocale()}
                      dateFormat={getStringFormatDate()}
                      timeFormat={false}
                      name="dtini"
                      value={dtini}
                      onChange={(date) => {
                        const formatted =
                          typeof date === "object" && date.format
                            ? date.format("DD/MM/YYYY")
                            : date;
                        setdtini(formatted);
                        setValue("dtini", formatted);
                      }}
                      inputProps={{
                        name: "dtini",
                        ref: register({ required: true }),
                        readOnly: "readonly",
                      }}
                      className={errors.dtini ? "error" : ""}
                    />
                    {errors.dtini && (
                      <FormHelperText className={classes.labelRootError}>
                        <span>{errors.dtini.message}</span>
                      </FormHelperText>
                    )}
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={6} md={3}>
                  <FormControl fullWidth className={classes.formControl} style={{ marginBottom: "20px" }}>
                    <label
                      className={
                        errors.dtfim
                          ? "select__label select__label_error"
                          : "select__label"
                      }
                    >
                      Data Final *
                    </label>
                    <Datetime
                      locale={getLocale()}
                      dateFormat={getStringFormatDate()}
                      timeFormat={false}
                      name="dtfim"
                      value={dtfim}
                      onChange={(date) => {
                        const formatted =
                          typeof date === "object" && date.format
                            ? date.format("DD/MM/YYYY")
                            : date;
                        setdtfim(formatted);
                        setValue("dtfim", formatted);
                      }}
                      inputProps={{
                        name: "dtfim",
                        ref: register({ required: true }),
                        readOnly: "readonly",
                      }}
                      className={errors.dtfim ? "error" : ""}
                    />
                    {errors.dtfim && (
                      <FormHelperText className={classes.labelRootError}>
                        <span>{errors.dtfim.message}</span>
                      </FormHelperText>
                    )}
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                  <FormControl fullWidth className={classes.formControl} style={{ marginBottom: "20px" }}>
                    <label className="select__label">Status do Fechamento</label>
                    <Select
                      classNamePrefix="select"
                      styles={ValidSelect(false)}
                      options={opcoesStatus}
                      value={statusFechamento}
                      onChange={setStatusFechamento}
                    />
                  </FormControl>
                </GridItem>
              </GridContainer>

              <GridContainer style={{ marginBottom: '20px'}}>
                <GridItem xs={12} sm={6} md={3}>
                  <FormControl fullWidth className={classes.formControl} style={{ marginBottom: "20px" }}>
                    <label className="select__label">
                      Situação do Fechamento
                    </label>
                    <Select
                      classNamePrefix="select"
                      styles={ValidSelect(false)}
                      options={opcoesSituacao}
                      value={situacaoFechamento}
                      onChange={setSituacaoFechamento}
                    />
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={6} md={3}>
                  <FormControl fullWidth className={classes.formControl} style={{ marginBottom: "20px" }}>
                    <label className="select__label">Tipo de Ação</label>
                    <Select
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                      styles={ValidSelect(false)}
                      placeholder={t("label.all")}
                      options={[{ value: null, label: "Todos" }, ...tiposAcao]}
                      value={tipoAcao}
                      onChange={setTipoAcao}
                    />
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <Button
                    round
                    color="primary"
                    onClick={(e) => {
                      handleClickPesquisa();
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <>
                        {t('label.search')}
                      </>
                    )}
                  </Button>
                  <Button
                    round
                    color="success"
                    onClick={downloadAuditoriaXlsx}
                    disabled={loadingXlsx || !resultado}
                    style={{ marginLeft: 8 }}
                  >
                    {loadingXlsx ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Exportar XLSX"
                    )}
                  </Button>
                </GridItem>

                {notification.show && (
                  <GridItem xs={12}>
                    <SnackbarContent
                      message={notification.message}
                      color={notification.color}
                      closeNotification={() =>
                        setNotification({ show: false })
                      }
                      close
                    />
                  </GridItem>
                )}
              </GridContainer>
              
            </CardBody>
          </Card>
        </GridItem>

        <GridItem xs={12}>
          <Card>
            <CardBody>
              {!buscaRealizada && !loading && (
                <p style={{ textAlign: "center", margin: "24px 0" }}>
                  Aplique os filtros e clique em Buscar para visualizar o
                  relatório.
                </p>
              )}

              {loading && (
                <div style={{ textAlign: "center", margin: "24px 0" }}>
                  <CircularProgress />
                  <p>{t("label.loading")}...</p>
                </div>
              )}

              {buscaRealizada &&
                !loading &&
                resultado &&
                resultado.total === 0 && (
                  <p style={{ textAlign: "center", margin: "24px 0" }}>
                    Nenhum registro encontrado para os filtros aplicados.
                  </p>
                )}

              {resultado && !loading && resultado.total > 0 && (
                <ReactTable
                  data={dadosTabela}
                  columns={columns}
                  defaultPageSize={10}
                  minRows={5}
                  showPaginationBottom={true}
                  sortable={false}
                  className="-striped -highlight"
                  previousText={t('button.previous')}
                  nextText={t('button.next')}
                  loadingText={t("label.loading") + "..."}
                  noDataText={t("information_message.no_records_found") + "!"}
                  pageText={t('label.page')}
                  ofText={t('label.of')}
                  rowsText={t('label.rows')}
                />
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
